import { FunctionComponent, h } from 'preact'
import { useEffect, useRef, useState } from 'preact/hooks'

import {
  CampaignType,
  Field,
  FieldEntry,
  FormFieldType,
  GraphQLError,
  RatingField,
  TextArea,
  TextareaFieldValue,
  TriggerEvent,
} from 'scripts/type'
import { getStorageFormSetting, getStorageKey, getUrlParam, postMessage, Storage } from 'scripts/utils'
import { ErrorPage } from 'components/ui'
import FeedbackForm from 'components/business/FeedbackForm'
import BaseContext from 'contexts/base-context'
import { useReceiveMessages } from 'scripts/hooks'
import queryCampaignFields from '../scripts/service/queryCampaignFields'
import fieldGroupByType from 'components/business/FeedbackForm/utils/fieldGroupByType'
import { patchPublishedEntry } from 'scripts/service'
import { MessageActions } from 'scripts/hooks/useReceiveMessages'
import { WIDGET_AUTO_CLOSE_DURATION } from 'scripts/constants'
import { WidgetWidth } from 'components/business/Preview/type'

export type Props = {
  token: string
  isUrlVisit?: boolean
}

const Feedback: FunctionComponent<Props> = (props) => {
  const token = props.token
  Storage.setItem(getStorageKey('urlPublishedFormToken', token), token)
  const { parentWindowSize } = useReceiveMessages(token)
  const [error, setError] = useState<GraphQLError>()
  const [currentField, setCurrentField] = useState<Field | null>(null)
  const submitLoadingRef = useRef(false)
  const [textareaValue, setTextareaValue] = useState<TextareaFieldValue>({ text: '', screenshots: [] })
  const [ratingField, setRatingField] = useState<RatingField | null>(null)
  const [textareaField, setTextareaField] = useState<TextArea | null>(null)
  const bakRef = useRef<Field | null>(null)
  const width = (getUrlParam('width') || WidgetWidth.SMALL) as WidgetWidth

  useEffect(() => {
    postMessage({ name: TriggerEvent.Loaded, data: { campaignToken: token } })
    queryFields()
    window.addEventListener(MessageActions.RE_RENDER_PAGE, () => {
      resetForm()
      setTimeout(() => {
        setRatingField(bakRef.current as RatingField)
        setCurrentField(bakRef.current)
      })
    })
  }, [])

  const queryFields = () => {
    queryCampaignFields({ campaignToken: token, type: CampaignType.FEEDBACK, isUrlVisit: props.isUrlVisit })
      .then((data) => {
        const groupFieldsResult = fieldGroupByType(data.nodes)
        setRatingField(groupFieldsResult.ratingField)
        setTextareaField(groupFieldsResult.textareaField)
        setCurrentField(data.nodes[0])
        bakRef.current = groupFieldsResult.ratingField
      })
      .catch((e) => {
        console.error(e)
        setError(e[0])
      })
  }

  const resetForm = () => {
    setTextareaValue({ text: '', screenshots: [] })
    setRatingField(null)
    setCurrentField(null)
  }

  if (error) {
    return <ErrorPage error={error} campaignType={CampaignType.FEEDBACK} />
  }

  const onRatingFieldChange = async (value: number) => {
    const fieldEntry = [
      {
        code: ratingField?.code + '',
        value: value,
      },
    ]
    await doPatchPublishedEntry(fieldEntry).then()
  }

  const onTextareaFieldChange = (value: TextareaFieldValue) => {
    setTextareaValue(value)
  }

  const onSubmit = async () => {
    if (submitLoadingRef.current) {
      return
    }
    const values = []
    textareaField &&
      values.push({
        code: textareaField.code + '',
        value: textareaValue,
      })
    await doPatchPublishedEntry(values)
  }

  const doPatchPublishedEntry = async (fieldsEntry: FieldEntry[]) => {
    try {
      submitLoadingRef.current = true
      const data = await patchPublishedEntry(fieldsEntry, token)
      setCurrentField(data.nodes[0])
      submitLoadingRef.current = false
    } catch (e) {
      setCurrentField({ type: FormFieldType.EndingField } as Field)
    }
  }

  const handleFieldChange = (height: number) => {
    if (currentField?.type === FormFieldType.RatingField) {
      postMessage({ name: TriggerEvent.FieldChange, data: { height: height, campaignToken: token } })
    }
    if (currentField?.type === FormFieldType.EndingField) {
      const publishedEntryId = Storage.getItem(getStorageKey('publishedEntryId', token))
      postMessage({ name: TriggerEvent.Complete, data: { campaignToken: token, entryId: publishedEntryId } })
      setTimeout(() => {
        postMessage({ name: TriggerEvent.Close, data: { campaignToken: token } })
      }, WIDGET_AUTO_CLOSE_DURATION)
    }
  }

  return (
    <BaseContext.Provider
      value={{
        parentWindowSize,
        interactive: true,
        isPreview: false,
        isUrlVisit: props.isUrlVisit,
        urlToken: token,
        formSetting: getStorageFormSetting(token),
        width,
      }}
    >
      {currentField && ratingField && (
        <FeedbackForm
          field={currentField}
          ratingField={ratingField}
          textareaField={textareaField}
          onRatingFieldChange={onRatingFieldChange}
          onTextAreaFieldChange={onTextareaFieldChange}
          onSubmit={onSubmit}
          onFieldChange={handleFieldChange}
        />
      )}
    </BaseContext.Provider>
  )
}

export default Feedback
